import React, { useMemo } from 'react';
import { Card, Typography, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { IconPlus, IconMinus, IconHeart, IconHeartFilled, IconTruck, IconBuildingStore } from "@tabler/icons-react";
import SafeImage from './SafeImage';
import { useProductSelection } from '../util/useProductSelection';
import { CartContext } from '../CartContext';
import { WishlistContext } from '../WishlistContext';
import AuthServ from '../service/AuthServ';
import { useGlobalAuthModal } from '../util/GlobalAuthModal';
import { formatUnit, capitalizeFirst, toTitleCase } from '../util/unitFormatter';
import { usePreference } from '../context/PreferenceContext';
import GlobalTooltip from './GlobalTooltip';
import '../css/productCard.css';

const { Title, Text } = Typography;

/**
 * ProductCard — receives an optional `formatETA` prop from the parent page.
 * The parent page should run a single `useDeliveryEstimate` for all shopIds
 * and pass its `formatETA` down, so all cards share one batched API call.
 * If formatETA is not provided, falls back to shop's stored deliveryTime.
 */
const ProductCard = ({ product, hideShopInfo = false, isHotDeal = false, formatETA: formatETAProp, viewType = 'grid' }) => {
    const { t } = usePreference();
    const navigate = useNavigate();
    const { cart, addToCart, updateQuantity, removeFromCart } = React.useContext(CartContext);
    const { toggleWishlist, isInWishlist } = React.useContext(WishlistContext);
    const { showAlert } = useGlobalAuthModal();

    const shopId = product.shopId?._id || product.shopId;
    // Use parent-provided formatETA (shared, batched); fallback to static DB value if not provided
    const formatETA = formatETAProp || ((sid, fallback) => fallback);
    const productId = product._id || product.id;
    const [selectedVariantId, setSelectedVariantId] = useProductSelection(productId);
    const [isManuallySelected, setIsManuallySelected] = React.useState(false);
    const [showAllVariants, setShowAllVariants] = React.useState(false);

    const [localShopStatus, setLocalShopStatus] = React.useState({
        isOpen: true,
        reopenMessage: ""
    });

    React.useEffect(() => {
        const handleShopStatusUpdate = (e) => {
            const data = e.detail;
            if (data?.shopId && String(data.shopId) === String(shopId)) {
                setLocalShopStatus({
                    isOpen: data.availability?.isOpen ?? true,
                    reopenMessage: data.availability?.reopenMessage || ""
                });
            }
        };

        window.addEventListener('shopStatusUpdated', handleShopStatusUpdate);
        return () => window.removeEventListener('shopStatusUpdated', handleShopStatusUpdate);
    }, [shopId]);

    const allVariants = useMemo(() => {
        let variants = [];
        if (product.options && product.options.length > 0) {
            product.options.forEach(opt => {
                if (opt.variants) variants.push(...opt.variants);
            });
        } else if (product.hasVariants && product.variants) {
            variants = product.variants;
        }
        return [...variants].sort((a, b) => (a.price || 0) - (b.price || 0));
    }, [product]);

    const currentVariant = useMemo(() => {
        if (!allVariants.length) return null;
        if (selectedVariantId) {
            const match = allVariants.find(v => String(v._id || v.val || v.value || v.name || v.size) === String(selectedVariantId));
            if (match) return match;
        }
        const dealMatch = allVariants.find(v => v.isHotDeal === true);
        if (dealMatch) return dealMatch;
        return allVariants[0];
    }, [allVariants, selectedVariantId]);

    React.useEffect(() => {
        if (allVariants.length && (!selectedVariantId || !allVariants.find(v => String(v._id || v.val || v.value || v.name || v.size) === String(selectedVariantId)))) {
            const dealMatch = allVariants.find(v => v.isHotDeal || v.hasActiveDeal);
            const unitsMatch = allVariants.find(v => {
                const val = (v.val || v.value || v.name || v.size || "").toString().toLowerCase();
                return val.includes("500") || val === "0.5" || val === ".5";
            });
            const targetVariant = dealMatch || unitsMatch || allVariants[0];
            const targetId = targetVariant?._id || targetVariant?.val || targetVariant?.value || targetVariant?.name || targetVariant?.size;
            if (targetId && String(selectedVariantId) !== String(targetId)) {
                setSelectedVariantId(targetId);
            }
        }
    }, [allVariants, selectedVariantId, setSelectedVariantId]);

    const displayPrice = currentVariant ? (currentVariant.pri !== undefined ? currentVariant.pri : currentVariant.price) : product.price;
    const displayMrp = currentVariant ? (currentVariant.mrp || product.mrp) : product.mrp;
    const displayImage = (isManuallySelected && currentVariant?.imageUrl)
        ? currentVariant.imageUrl
        : (product.imageUrl || (product.images && product.images.length > 0 ? product.images[0] : null));

    // Stock computation
    const effectiveStock = useMemo(() => {
        if (currentVariant) {
            const vStock = currentVariant.stc !== undefined ? currentVariant.stc : currentVariant.stock;
            if (vStock !== undefined) return vStock;
        }
        return product.stock ?? 0;
    }, [currentVariant, product.stock]);

    const isOutOfStock = effectiveStock <= 0;

    const stockLabel = useMemo(() => {
        if (isOutOfStock) return null; // handled by overlay
        if (effectiveStock <= 5) return { text: `Only ${effectiveStock} left!`, type: 'urgent' };
        if (effectiveStock <= 20) return { text: 'Limited stock', type: 'warning' };
        return null;
    }, [effectiveStock, isOutOfStock]);

    const discount = useMemo(() => {
        if (currentVariant?.discount !== undefined && currentVariant?.discount !== null) return currentVariant.discount;
        return (displayMrp && displayPrice && displayMrp > displayPrice)
            ? Math.round(((displayMrp - displayPrice) / displayMrp) * 100)
            : 0;
    }, [currentVariant, displayMrp, displayPrice]);

    const cartItem = useMemo(() => {
        if (!cart?.items) return null;
        return cart.items.find(item => {
            const sameProduct = (item.product?._id || item.product) === productId;
            if (!sameProduct) return false;

            // Check variant match if current variant exists
            if (currentVariant) {
                const itemOptions = item.selectedOptions || {};
                const variantValue = String(currentVariant.val || currentVariant.value).toLowerCase();
                return Object.values(itemOptions).some(val => String(val).toLowerCase() === variantValue);
            }
            return true;
        });
    }, [cart, productId, currentVariant]);

    const isWishlisted = isInWishlist(productId);

    const handleIncrease = async (e) => {
        e.stopPropagation();
        if (cartItem) {
            await updateQuantity(cartItem._id, cartItem.quantity + 1);
        }
    };

    const handleDecrease = async (e) => {
        e.stopPropagation();
        if (cartItem) {
            if (cartItem.quantity > 1) {
                await updateQuantity(cartItem._id, cartItem.quantity - 1);
            } else {
                await removeFromCart(cartItem._id);
            }
        }
    };

    const handleAddToCart = async (e) => {
        e.stopPropagation();

        const options = {};
        if (currentVariant) {
            const val = currentVariant.val || currentVariant.value || currentVariant.name || currentVariant.size || "Default";
            const group = product.options?.find(o => o.variants?.some(v => String(v._id || v.val || v.value || v.name || v.size) === String(currentVariant._id || val)));
            if (group) options[group.name] = val;
            else if (product.variantType) options[product.variantType] = val;
            else options["Type"] = val;
        }
        await addToCart(productId, 1, options, {
            name: product.name,
            image: displayImage,
            variant: currentVariant ? formatUnit(currentVariant.val || currentVariant.value, product.variantType || "Option") : null,
            price: displayPrice,
            mrp: displayMrp,
            shopId: product.shopId?._id || product.shopId || product.shop?._id || product.shop
        });
    };

    const handleWishlist = (e) => {
        e.stopPropagation();
        if (!AuthServ.isLogged()) {
            showAlert({
                title: t("login_required"),
                description: t("login_to_add_to_cart_desc"),
                buttons: [
                    { text: t("cancel"), variant: "default" },
                    { text: t("login"), variant: "primary", onClick: () => navigate("/customer-login") }
                ]
            });
            return;
        }
        toggleWishlist(productId);
    };

    const goToProduct = () => {
        const queryParams = new URLSearchParams();
        if (product.price) queryParams.set("dealPrice", product.price);
        if (currentVariant?._id || currentVariant?.value) {
            queryParams.set("variantId", currentVariant?._id || currentVariant?.value);
        }

        const url = `/single/Products/${productId}?${queryParams.toString()}`;
        window.open(url, '_blank');
    };

    return (
        <Card
            hoverable
            className={`product-card-unified pcu-card-body-no-padding ${isHotDeal ? 'hot-deal' : ''} ${viewType === 'list' ? 'pcu-list-view' : ''}`}
            onClick={goToProduct}
        >
            <div className="pcu-image-container">
                <SafeImage src={displayImage} alt={product.name} variant="square" />

                {/* {discount > 0 && (
                    <div className="pcu-offer-badge">
                        {discount}% OFF
                    </div>
                )} */}

                <div className={`pcu-wishlist-icon ${isWishlisted ? 'active' : ''}`} onClick={handleWishlist}>
                    {isWishlisted ? <IconHeartFilled size={14} /> : <IconHeart size={14} />}
                </div>

                <div className="pcu-add-btn-wrapper">
                    {!localShopStatus.isOpen ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: '4px' }}>
                            <Button className="pcu-add-btn pcu-add-btn-disabled" disabled style={{ width: '100%', background: '#f5f5f5', color: '#999', opacity: 0.8 }}>
                                Unavailable
                            </Button>
                        </div>
                    ) : isOutOfStock ? (
                        <Button className="pcu-add-btn pcu-add-btn-disabled" disabled>
                            {t("out_of_stock")}
                        </Button>
                    ) : cartItem ? (
                        <div className="pcu-quantity-controls-overlay">
                            <Button
                                className="pcu-qty-btn"
                                icon={<IconMinus size={14} />}
                                onClick={handleDecrease}
                            />
                            <span className="pcu-qty-value">{cartItem.quantity}</span>
                            <Button
                                className="pcu-qty-btn"
                                icon={<IconPlus size={14} />}
                                onClick={handleIncrease}
                            />
                        </div>
                    ) : (
                        <Button
                            className="pcu-add-btn"
                            onClick={handleAddToCart}
                        >
                            {t("add").toUpperCase()}
                        </Button>
                    )}
                </div>
            </div>

            <div className="pcu-content">
                {viewType === 'list' && (product.shopName || product.shopId?.name || product.shop?.name) && (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            if (shopId) navigate(`/products/${shopId}`);
                        }}
                        style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    >
                        <IconBuildingStore size={12} style={{ marginRight: '4px' }} />
                        {toTitleCase(product.shopName || product.shopId?.name || product.shop?.name)}
                    </div>
                )}
                <Title level={5} className="pcu-title">
                    {capitalizeFirst(product.name)}
                </Title>
                {(product.estimatedDeliveryTime || formatETA(shopId, product.shopId?.business?.deliveryTime || product.shopId?.deliveryTime || product.shop?.deliveryTime || "30-45 mins")) && (
                    <div className="pcu-delivery-wrapper">
                        <div className="pcu-delivery-badge-orange">
                            Delivery By {product.estimatedDeliveryTime || formatETA(shopId, product.shopId?.business?.deliveryTime || product.shopId?.deliveryTime || product.shop?.deliveryTime || "30-45 mins")}
                        </div>
                    </div>
                )}

                {/* {product.description && (
                    <GlobalTooltip title={product.description} placement="topLeft">
                        <Text className="pcu-description" ellipsis={{ rows: 1 }}>
                            {capitalizeFirst(product.description)}
                        </Text>
                    </GlobalTooltip>
                )} */}

                {product?.rating > 0 && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "3px",
                        }}
                    >
                        <span
                            style={{
                                color: "#ffc107",
                                fontSize: "15px",
                            }}
                        >
                            {"★".repeat(Math.round(product.rating))}
                        </span>
                    </div>
                )}
                <div className="pcu-price-row">
                    <span className="pcu-price">
                        ₹{Math.round(displayPrice).toLocaleString()}
                    </span>
                    {displayMrp > displayPrice && (
                        <>
                            <span className="pcu-mrp">MRP ₹{Math.round(displayMrp).toLocaleString()}</span>
                            {/* <span className="pcu-save-badge">
                                SAVE ₹{Math.round(displayMrp - displayPrice).toLocaleString()}
                            </span> */}
                        </>
                    )}
                </div>



                <div className="pcu-variant-selector-wrapper">
                    {allVariants.length > 0 && (
                        <div className="pcu-variant-pills-container">
                            {allVariants.slice(0, showAllVariants ? allVariants.length : 1).map(v => {
                                const isSelected = String(v._id || v.value) === String(currentVariant?._id || currentVariant?.value);
                                return (
                                    <div
                                        key={v._id || v.val || v.value}
                                        className={`pcu-variant-pill ${isSelected ? 'active' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedVariantId(v._id || v.val || v.value);
                                            setIsManuallySelected(true);
                                        }}
                                    >
                                        {formatUnit(v.val || v.value, product.variantType || "Option")}
                                    </div>
                                );
                            })}
                            {!showAllVariants && allVariants.length > 1 && (
                                <div
                                    className="pcu-variant-pill"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowAllVariants(true);
                                    }}
                                >
                                    +{allVariants.length - 2} more
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {viewType !== 'list' && (product.shopName || product.shopId?.name || product.shop?.name) && (
                <div
                    className="pcu-shop-name"
                    onClick={(e) => {
                        e.stopPropagation();
                        if (shopId) navigate(`/products/${shopId}`);
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    <IconBuildingStore size={12} className="pcu-shop-icon" />
                    <span className="pcu-shop-text">
                        {toTitleCase(product.shopName || product.shopId?.name || product.shop?.name)}
                    </span>
                </div>
            )}
        </Card>
    );
};

export default React.memo(ProductCard);
